import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { IBadge, IAsset } from '../types/damTypes';
import { getBadges, setBadgesForAsset } from '../services/badgeService';
import axiosInstance from '../services/axiosInstance';

interface AssetBadgeManagerProps {
  open: boolean;
  onClose: () => void;
  asset: IAsset | null;
  onBadgesUpdated?: (updatedAsset: IAsset) => void;
  assetIds?: string[] | null;
  onBatchBadgesUpdated?: () => void;
}

type BadgeActionSelection = 'add' | 'remove' | 'none';
type BadgeActionType = Exclude<BadgeActionSelection, 'none'>;

const AssetBadgeManager: React.FC<AssetBadgeManagerProps> = ({
  open,
  onClose,
  asset,
  onBadgesUpdated,
  assetIds = null,
  onBatchBadgesUpdated
}) => {
  const [availableBadges, setAvailableBadges] = useState<IBadge[]>([]);
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const [badgeActions, setBadgeActions] = useState<Record<string, BadgeActionType>>({});
  const [overviewLoading, setOverviewLoading] = useState(false);
  const normalizedAssetIds = assetIds && assetIds.length > 0 ? assetIds : (asset ? [asset._id] : []);
  const selectionCount = normalizedAssetIds.length || (asset ? 1 : 0);
  const isBatchMode = normalizedAssetIds.length > 1;

  // Load badges when modal opens
  useEffect(() => {
    if (open) {
      loadBadges();
    }
  }, [open]);

  useEffect(() => {
    if (open && asset && !isBatchMode) {
      const currentBadgeIds = asset.activeBadges.map(badge => 
        typeof badge === 'string' ? badge : badge._id
      );
      setSelectedBadgeIds(currentBadgeIds);
    } else if (isBatchMode) {
      setSelectedBadgeIds([]);
    }
  }, [open, asset, isBatchMode]);

  useEffect(() => {
    if (!open || !isBatchMode || normalizedAssetIds.length === 0) {
      setBadgeCounts({});
      setBadgeActions({});
      return;
    }
    loadBadgeOverview(normalizedAssetIds);
  }, [open, isBatchMode, normalizedAssetIds.join(',')]);

  const loadBadges = async () => {
    setLoading(true);
    setError(null);
    try {
      const badges = await getBadges();
      setAvailableBadges(badges.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (err: any) {
      setError('Failed to load badges: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBadgeOverview = async (ids: string[]) => {
    if (ids.length === 0) return;
    setOverviewLoading(true);
    try {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const resp = await axiosInstance.get(`/api/assets/${id}`);
            const active = Array.isArray(resp.data?.activeBadges) ? resp.data.activeBadges : [];
            return active
              .map((badge: any) => (typeof badge === 'string' ? badge : badge?._id))
              .filter((badgeId: string | undefined) => !!badgeId);
          } catch {
            return [];
          }
        })
      );
      const counts: Record<string, number> = {};
      results.forEach((badgeList: string[]) => {
        badgeList.forEach((badgeId: string) => {
          counts[badgeId] = (counts[badgeId] || 0) + 1;
        });
      });
      setBadgeCounts(counts);
    } catch (err: any) {
      setError('Failed to load badge overview: ' + (err.response?.data?.message || err.message));
    } finally {
      setOverviewLoading(false);
    }
  };

  const getBadgeStatus = (badgeId: string): 'all' | 'some' | 'none' => {
    if (selectionCount === 0) return 'none';
    const count = badgeCounts[badgeId] || 0;
    if (count === 0) return 'none';
    if (count === selectionCount) return 'all';
    return 'some';
  };

  const getBadgeStatusText = (badgeId: string) => {
    if (selectionCount === 0) return '';
    const count = badgeCounts[badgeId] || 0;
    if (count === 0) return 'Ingen av de valda';
    if (count === selectionCount) return 'Alla valda';
    return `${count}/${selectionCount} valda`;
  };

  const getBadgeActionValue = (badgeId: string): BadgeActionSelection => {
    return badgeActions[badgeId] || 'none';
  };

  const handleBadgeActionChange = (badgeId: string, action: BadgeActionSelection) => {
    setBadgeActions(prev => {
      const next = { ...prev };
      if (action === 'none') {
        delete next[badgeId];
      } else {
        next[badgeId] = action;
      }
      return next;
    });
  };

  const handleBadgeToggle = (badgeId: string) => {
    setSelectedBadgeIds(prev => {
      if (prev.includes(badgeId)) {
        return prev.filter(id => id !== badgeId);
      } else {
        return [...prev, badgeId];
      }
    });
  };

  const handleSave = async () => {
    if (isBatchMode) {
      const badgesToAdd = Object.entries(badgeActions)
        .filter(([, action]) => action === 'add')
        .map(([badgeId]) => badgeId);
      const badgesToRemove = Object.entries(badgeActions)
        .filter(([, action]) => action === 'remove')
        .map(([badgeId]) => badgeId);

      if (badgesToAdd.length === 0 && badgesToRemove.length === 0) {
        setError('Välj minst en badge att lägga till eller ta bort');
        return;
      }

      setSaving(true);
      setError(null);

      try {
        await axiosInstance.post('/api/assets/batch/badges', {
          assetIds: normalizedAssetIds,
          addBadgeIds: badgesToAdd,
          removeBadgeIds: badgesToRemove
        });
        onBatchBadgesUpdated?.();
        onClose();
        setBadgeActions({});
        setBadgeCounts({});
      } catch (err: any) {
        setError('Failed to update badges: ' + (err.response?.data?.message || err.message));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!asset) return;

    setSaving(true);
    setError(null);

    try {
      await setBadgesForAsset(asset._id, selectedBadgeIds);
      
      // Update the asset with new badges
      const updatedAsset: IAsset = {
        ...asset,
        activeBadges: selectedBadgeIds
      };
      
      if (onBadgesUpdated) {
        onBadgesUpdated(updatedAsset);
      }
      
      onClose();
    } catch (err: any) {
      setError('Failed to update badges: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!saving) {
      setBadgeActions({});
      setBadgeCounts({});
      onClose();
    }
  };

  const renderBadgePreview = (badge: IBadge) => (
    <Chip
      label={badge.name}
      size="small"
      style={{
        backgroundColor: badge.color,
        color: badge.textColor,
        fontWeight: 'bold',
        marginLeft: '8px'
      }}
    />
  );

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isBatchMode ? `Edit badges (${selectionCount} assets)` : 'Manage Badges for Asset'}
        {!isBatchMode && asset && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {asset.fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isBatchMode ? (
          overviewLoading || loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : availableBadges.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No badges available. Create badges in the Badge Management settings first.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which badges to add or remove for the selected assets.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {availableBadges.map(badge => {
                  const statusText = getBadgeStatusText(badge._id);
                  const actionValue = getBadgeActionValue(badge._id);
                  const removeDisabled = getBadgeStatus(badge._id) === 'none';
                  return (
                    <Box 
                      key={badge._id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 1.5,
                        backgroundColor: 'white'
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{badge.name}</Typography>
                          {renderBadgePreview(badge)}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {statusText}
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        value={actionValue}
                        exclusive
                        size="small"
                        onChange={(_, value) => handleBadgeActionChange(badge._id, (value || 'none') as BadgeActionSelection)}
                      >
                        <ToggleButton value="none">Behåll</ToggleButton>
                        <ToggleButton value="add">Lägg till</ToggleButton>
                        <ToggleButton value="remove" disabled={removeDisabled}>Ta bort</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  );
                })}
              </Box>
            </>
          )
        ) : loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : availableBadges.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No badges available. Create badges in the Badge Management settings first.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select badges to assign to this asset:
            </Typography>
            
            <FormGroup>
              {availableBadges.map((badge) => (
                <Box key={badge._id} sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedBadgeIds.includes(badge._id)}
                        onChange={() => handleBadgeToggle(badge._id)}
                        disabled={saving}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2">{badge.name}</Typography>
                        {renderBadgePreview(badge)}
                      </Box>
                    }
                  />
                </Box>
              ))}
            </FormGroup>

            {selectedBadgeIds.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Preview - Selected badges:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedBadgeIds.map(badgeId => {
                    const badge = availableBadges.find(b => b._id === badgeId);
                    return badge ? renderBadgePreview(badge) : null;
                  })}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || loading || (isBatchMode && Object.keys(badgeActions).length === 0)}
        >
          {saving ? <CircularProgress size={24} /> : isBatchMode ? 'Apply Changes' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetBadgeManager; 